import React, { useContext } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import BackArrow from "@material-ui/icons/ChevronLeft";
import ForwardArrow from "@material-ui/icons/ChevronRight";
import { AppContext, SET_OCCASIONFILTER } from "../Context";
import { getDays } from "./util";
import { UnstyledButton } from "@mantine/core";

const useStyles = makeStyles((theme) => ({
  selector: {
    width: "410px",
    margin: "0 auto",
    "@media(max-width: 667px)": {
      width: "340px",
    },
    "@media(max-width: 340px)": {
      width: "305px",
    },
  },
  weekYearContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: "16px",
    padding: "0 34px",
    color: theme.app.labelColor,
  },
  year: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  week: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    textAlign: "right",
    textTransform: "uppercase",
  },
  selectorContainer: {
    display: "grid",
    gridTemplateColumns: "30px 350px 30px",
    "@media(max-width: 667px)": {
      gridTemplateColumns: "30px 280px 30px",
    },
    "@media(max-width: 340px)": {
      gridTemplateColumns: "30px 245px 30px",
    },
  },
  disabled: {
    opacity: 0.5,
  },
  dayContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 50px)",
    "@media(max-width: 667px)": {
      gridTemplateColumns: "repeat(7, 40px)",
    },
    "@media(max-width: 340px)": {
      gridTemplateColumns: "repeat(7, 35px)",
    },
  },
  item: {
    display: "grid",
    gridTemplateColumns: "1fr",
    justifyItems: "center",  
    border: `1px solid ${theme.app.labelColor}`,
    borderLeft: "none",
    backgroundColor: theme.app.background[1],
    color: theme.app.labelColor,
    "&:first-child": {
      borderRadius: "4px 0 0 4px",
      borderLeft: `1px solid ${theme.app.labelColor}`,
      "& button": {
        borderRadius: "4px 0 0 4px",
      },
    },
    "&:last-child": {
      borderRadius: "0 4px 4px 0",
      "& button": {
        borderRadius: "0 4px 4px 0",
      },
    },
  },
  today: {
    color: theme.app.primaryColors.main,
  },
  itemButton: {
    height: "100%",
    width: "100%",
    padding: "2px",
    color: theme.app.labelColor,
    backgroundColor: theme.app.background[1],
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:disabled": {
      // opacity: 0.5
    },
  },
  selected: {
    color: theme.app.labelColor,
    backgroundColor: theme.app.background[3],
  },
  date: {
    fontSize: "1.0625rem",
    fontWeight: 500,
    lineHeight: 1,
    color: theme.app.labelColor,
    "@media(max-width: 340px)": {
      fontSize: "0.875rem",
    },
  },
  weekday: {
    color: theme.app.labelColor,
    fontSize: "0.625rem",
    textTransform: "uppercase",
    fontWeight: 500,
  },
  holiday: {
    color: theme.app.dangerColor,
  },
  arrowButton: {
    color: theme.app.labelColor,
    "&:focus": {
      outline: `1px solid ${theme.app.primaryColors.main}`,
      borderRadius: "4px",
    },
  },
}));

const Selector = (props) => {
  const { anyPrevious, week, loading } = props;
  const classes = useStyles();
  const language = useSelector((state) => state.language);
  const context = useContext(AppContext);
  const { state, dispatch } = context;
  const { occasionFilter } = state;
  const setFilter = (type, value, checked) =>
    dispatch({ type: SET_OCCASIONFILTER, payload: { type, value, checked } });
  let occasionDates = [];
  week.occasions.forEach((o) => {
    const date = moment(o.at).format("YYYYMMDD");
    if (!occasionDates.includes(date)) {
      occasionDates = [...occasionDates, date];
    }
  });
  const days = getDays(week, occasionDates);
  return (
    <div className={classes.selector}>
      <div className={classes.weekYearContainer}>
        <Typography component="div" className={classes.year}>
          {`${moment(days[0].date).format("MMM")} ${week.thisYear}`}
        </Typography>
        <Typography component="div" className={classes.week}>
          {`${language.shortWeek}${week.thisWeek}`}
        </Typography>
      </div>
      <div className={classes.selectorContainer}>
        <ButtonBase
          component={Link}
          to={`/week/${week.prevYear}/${week.prevWeek}`}
          disabled={!anyPrevious || loading}
          classes={{ disabled: classes.disabled }}
          className={classes.arrowButton}
          aria-label={language.previous_week}
        >
          <BackArrow />
        </ButtonBase>
        <div className={classes.dayContainer}>
          {days.map((d, i) => {
            const selected = occasionFilter.weekdays.includes(d.value);
            return (
              <div
                className={classNames(classes.item, d.isToday && classes.today)}
                key={i}
              >
                <UnstyledButton
                  className={(classes.itemButton, selected && classes.selected)}
                  disabled={!d.hasOccasions}
                  onClick={() => setFilter("weekdays", d.value, !selected)}
                  sx={{width: "100%", height: "100%", padding: "0.35rem 0.25rem 0.25rem 0.25rem", textAlign: "center"}}
                  aria-label={`${language.addToFilter} ${moment(d.date).format("ddd")} ${moment(d.date).format("DD")} ${moment(d.date).format("MMMM")} ${language.toFilter}`}
                >
                  <div>
                    <Typography
                      className={classNames(
                        classes.date,
                        d.isHoliday && classes.holiday
                      )}
                    >
                      {moment(d.date).format("DD")}
                    </Typography>
                    <Typography
                      className={classNames(
                        classes.weekday,
                        d.isHoliday && classes.holiday
                      )}
                    >
                      {moment(d.date).format("ddd")}
                    </Typography>
                  </div>
                </UnstyledButton>
              </div>
            );
          })}
        </div>
        <ButtonBase
          component={Link}
          to={`/week/${week.nextYear}/${week.nextWeek}`}
          disabled={loading}
          className={classes.arrowButton}
          classes={{ disabled: classes.disabled }}
          aria-label={language.next_week}
        >
          <ForwardArrow />
        </ButtonBase>
      </div>
    </div>
  );
};

export default Selector;
